import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Star } from "assets/images/svgs/attendee/attende-star.svg";
import { ReactComponent as FavouriteChart } from "assets/images/svgs/attendee/attende-favorite-chart.svg";
import { ReactComponent as User } from "assets/images/svgs/attendee/attende-profile-2user.svg";
import { ReactComponent as BlueStart } from "assets/images/svgs/attendee/blue-starr.svg";
import { ReactComponent as EditSvg } from "assets/images/svgs/attendee/edit-2.svg";
import { ReactComponent as FavoriteChartDark } from "assets/images/svgs/attendee/favoriteChart.svg";
import { ReactComponent as FolderOpenSvg } from "assets/images/svgs/attendee/folder-open.svg";
import { ReactComponent as SettingSvg } from "assets/images/svgs/attendee/setting-4.svg";
import { ReactComponent as AwardLink } from "assets/images/svgs/award-link.svg";
import { ReactComponent as FavoriteChart } from "assets/images/svgs/attendee/user-tagg.svg";
import { ReactComponent as Logout } from "assets/images/svgs/attendee/logout.svg";
import useStore from "hooks/useStore";
import { siteRoutes } from "utils/helpers/enums/routes";
import {
  differenceInYears,
  differenceInMonths,
  differenceInDays,
} from "date-fns";
import { useSelector } from "react-redux";
import { IoIosCloseCircleOutline } from "react-icons/io";
import profileGif from "assets/images/gifs/add.gif";
import { CommunityIcon, SupportAttendee } from "assets/images/svgs";
import CustomerSupport from "../CustomerSupport";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const AttendeeNavbarDrawer: React.FC<DrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const {
    getUserFullName,
    getTotalEvents,
    getTotalMembers,
    getUserProfilePic,
    getPastEventsCreatedAt,
    isProfileCompleted,
  } = useStore();
  const userProfilePic = getUserProfilePic();
  const userFullName = getUserFullName();
  const totalEvents = getTotalEvents();
  const totalMembers = getTotalMembers();
  const pastEventsCreatedAt = getPastEventsCreatedAt();
  const [profileCompleted, setProfileCompleted] = useState<boolean | null>(
    null
  );
  const [showCompleteProfileModal, setShowCompleteProfileModal] =
    useState(false);

  const [showSupportDrawer, setShowSupportDrawer] = useState(false);

  const registeredData = useSelector(
    (state: any) => state.sharedReducer.registeredData
  );

  useEffect(() => {
    if (registeredData) {
      const completed = registeredData?.profiles?.attendee?.profileCompleted;
      setProfileCompleted(completed);
    }
  }, [registeredData]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      // Reset modal state when drawer closes
      setShowCompleteProfileModal(false);
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const now = new Date();
  const createdAt = new Date(pastEventsCreatedAt);
  const years = differenceInYears(now, createdAt);
  const months = differenceInMonths(now, createdAt);
  const days = differenceInDays(now, createdAt);
  let timeSince = "";
  if (years >= 1) {
    timeSince = `${years} year${years > 1 ? "s" : ""}`;
  } else if (months >= 1) {
    timeSince = `${months} month${months > 1 ? "s" : ""}`;
  } else {
    timeSince = `${days} day${days > 1 ? "s" : ""}`;
  }

  const handleNavigation = (item: any) => {
    if (item.label === "Logout") {
      localStorage.clear();
      onClose();
      navigate(item.link);
      return;
    }

    if (item.label === "Edit Profile") {
      if (profileCompleted === false || profileCompleted === null) {
        onClose();
        navigate(siteRoutes.parentGuardianDetails);
        return;
      } else {
        onClose();
        navigate(item.link);
        return;
      }
    }

    if (
      item.label === "My Experiences" ||
      item.label === "Resource Library" ||
      item.label === "Favorite Hosts" ||
      item.label === "General Settings" ||
      item.label === "Community" ||
      item.label === "Family Member" ||
      item.label === "Experiences"
    ) {
      if (profileCompleted === false || profileCompleted === null) {
        setShowCompleteProfileModal(true);
      } else {
        onClose();
        navigate(item.link);
      }
      return;
    }

    if (item.label === "Customer Support") {
      setShowSupportDrawer(true);
      return;
    }

    onClose();
    navigate(item.link);
  };

  const handleFamilyDetailsNavigation = () => {
    if (profileCompleted === false || profileCompleted === null) {
      setShowCompleteProfileModal(true);
    } else {
      onClose();
      navigate(siteRoutes.familyMembers);
    }
  };

  return (
    <Fragment>
      <div
        className={`fixed inset-0 bg-black bg-opacity-30 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        style={{ zIndex: "calc(var(--z-drawer) + 1)" }}
        onClick={onClose}
      >
        <div
          className={`fixed top-0 right-0 h-full w-4/5 sm:w-1/2 md:w-1/3 lg:w-[465px] overflow-y-auto bg-white p-4 md:p-6 lg:p-10 shadow-lg transition-transform duration-300 ease-in-out transform ${
            isOpen ? "translate-x-0" : "translate-x-full"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-2xl text-gray-600 hover:text-black"
          >
            &times;
          </button>

          <div className="flex items-center space-x-4 mt-6">
            <img
              src={userProfilePic}
              alt="Profile"
              className="w-20 h-20 rounded-full object-cover"
            />
            <div>
              <h2 className="text-lg font-semibold text-primaryBlueText capitalize">
                {userFullName}
              </h2>
              {/* <span className="flex items-center justify-center text-sm w-fit px-3 h-[34px] rounded-full bg-[#F0F2F3] text-primaryBlueText">
              <BlueStart className="mr-1" /> 1 Year Member
            </span> */}
            </div>
          </div>

          {/* Stats Section */}
          <div className="flex items-center gap-2 sm:gap-2 lg:justify-between flex-wrap lg:flex-nowrap bg-attendeBlueBg rounded-lg lg:rounded-full px-3 py-3 mt-6">
            <span className="flex items-center text-base whitespace-nowrap text-primaryBlueText">
              <Star className="mr-1" />
              {timeSince} Member
            </span>
            <span className="flex items-center text-base whitespace-nowrap text-primaryBlueText">
              <User className="mr-1" /> {totalMembers} Members
            </span>
            <span className="flex items-center whitespace-nowrap text-base text-primaryBlueText">
              <FavouriteChart className="mr-1" /> {totalEvents} Exp.
            </span>
          </div>

          <div
            className="bg-attendeBlueBg rounded-lg cursor-pointer lg:rounded-full px-3 py-3 mt-3"
            onClick={handleFamilyDetailsNavigation}
          >
            <span className="flex items-center justify-center text-base whitespace-nowrap text-primaryBlueText">
              <BlueStart className="mr-1" /> View Family Details
            </span>
          </div>

          <div className="mt-3 flex flex-col">
            {/* Only visible on screens smaller than md */}
            <div className="md:hidden flex flex-col">
              {[
                {
                  label: "Community",
                  icon: <CommunityIcon />,
                  link: "/attendee/communities",
                },
                // {
                //   label: "Family Member",
                //   icon: <FavoriteChart />,
                //   link: "/attendee/family-members",
                // },

                // {
                //   label: "Experiences",
                //   icon: <AwardLink />,
                //   link: "/attendee/experiences",
                // },
              ].map((item, index) => (
                <div
                  key={index}
                  onClick={() => handleNavigation(item)}
                  className="flex py-5 items-center w-full text-left text-darkBlueText bordr-linear cursor-pointer"
                >
                  <span className="mr-3 text-lg">{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>

            {/* Other links (visible on all screen sizes) */}
            <div className="flex flex-col">
              {[
                {
                  label: "Edit Profile",
                  icon: <EditSvg />,
                  link: "/attendee/edit-profile",
                },
                {
                  label: "My Experiences",
                  icon: <FavoriteChartDark />,
                  link: "/attendee/experiences",
                },
                {
                  label: "Resource Library",
                  icon: <FolderOpenSvg />,
                  link: "/attendee/resource-library",
                },
                {
                  label: "Favorite Hosts",
                  icon: <FavoriteChart />,
                  link: siteRoutes.attendeeFavoriteHosts,
                },
                {
                  label: "Customer Support",
                  icon: (
                    // You can replace this with a more appropriate icon if needed.
                    // <Support />
                    <SupportAttendee />
                  ),
                  link: "#",
                },
                {
                  label: "General Settings",
                  icon: <SettingSvg />,
                  link: "/attendee/general-settings",
                },
                { label: "Logout", icon: <Logout />, link: "/auth/login" },
              ].map((item, index, array) => (
                <div
                  key={index}
                  onClick={() => handleNavigation(item)}
                  className={`flex  items-center w-full text-left text-darkBlueText cursor-pointer ${
                    index === array.length - 1 ? " pt-5" : "bordr-linear py-5"
                  }`}
                >
                  <span className="mr-3 text-lg">{item.icon}</span>
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Profile Completion Modal */}
      {showCompleteProfileModal && (
        <div className="fixed inset-0 flex  items-center justify-center bg-black bg-opacity-50" style={{ zIndex: "calc(var(--z-drawer) + 1)" }}>
          <div className="bg-white p-6 rounded-lg shadow-lg w-[85%] sm:w-[364px] text-center">
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowCompleteProfileModal(false);
                }}
                className="text-secondaryText text-3xl rounded"
              >
                <IoIosCloseCircleOutline />
              </button>
            </div>
            <div className="flex justify-center">
              <img
                src={profileGif}
                alt="Complete Profile"
                className="w-32 mx-auto mb-2"
              />
            </div>
            <h2 className="text-base text-primaryBlueText font-semibold mb-4">
              Complete your profile
            </h2>
            <p className="text-labelTextColor text-sm mb-4">
              Complete your profile to register for events and get personalized
              Recommendations.
            </p>
            <button
              onClick={() => {
                navigate(siteRoutes.parentGuardianDetails);
                setShowCompleteProfileModal(false);
                onClose();
              }}
              className="bg-primaryBlueText text-white px-4 py-2 rounded-full"
            >
              Continue
            </button>
          </div>
        </div>
      )}
      {showSupportDrawer && (
        <CustomerSupport
          isOpen={showSupportDrawer}
          onClose={() => setShowSupportDrawer(false)}
        />
      )}
    </Fragment>
  );
};

export default AttendeeNavbarDrawer;
